import { Alert, Box, Typography, styled } from '@mui/material';

import type { IAppDetailsAppData, IPlatform } from '../common-types';
import { platformNameConverter } from '../common-utilities';
import { AppScreenshotImage } from '@orcha/micro-library-core-ui';
import { Gallery } from '../Gallery';
import type { ImageProps } from 'next/image';

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(3)} 0`,
  overflow: 'hidden',
}));

export type PlatformGalleryProps = IPlatform & {
  app: IAppDetailsAppData;
  fallbackImageURL?: string;
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  translatedText: {
    platformGalleryHeading: string;
    platformGalleryMissingImages: string;
  };
};

/**
 * PlatformGallery from  [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param translatedText is required, this is the text that as already been translated by the consumer of this component.
 * @param FlexibleImageComponent is required, this is the component that is used to display the image.
 */

export const PlatformGallery = ({
  platformName,
  app,
  fallbackImageURL = '',
  translatedText,
  FlexibleImageComponent,
  ...props
}: PlatformGalleryProps) => {
  const { platforms } = app ?? {};
  const relevantPlatform = platforms?.find(
    (p) => platformNameConverter(p.platformName) === platformName
  );

  const imageUrls =
    relevantPlatform?.screenshotImageUrls?.filter((x) => x !== '') ?? [];

  return (
    <Container {...props}>
      <Box sx={{ gap: (theme) => theme.spacing(2) }}>
        <Typography component="h2" variant="h6">
          {translatedText?.platformGalleryHeading}
        </Typography>

        {imageUrls && imageUrls.length > 0 ? (
          <Gallery
            translatedText={{
              galleryRightArrowTitle:
                translatedText?.platformGalleryHeading ?? '',
              galleryLeftArrowTitle:
                translatedText?.platformGalleryMissingImages ?? '',
            }}
          >
            {imageUrls.map((src, index) => {
              return (
                <AppScreenshotImage
                  key={`screenshot-${index + 1}`}
                  src={src}
                  alt={`Screenshot ${index + 1}`}
                  fallbackImageURL={fallbackImageURL}
                  FlexibleImageComponent={FlexibleImageComponent}
                />
              );
            })}
          </Gallery>
        ) : (
          <Alert severity="info">
            {translatedText?.platformGalleryMissingImages}
          </Alert>
        )}
      </Box>
    </Container>
  );
};
